import _Vue from 'vue'

/**
 * Close open menus when the page scrolls.
 *
 * Vuetify positions a `v-menu` once, at open time, into a portal at the app root — it is
 * absolutely positioned in document coordinates and does not follow its activator afterwards.
 * Scroll the page and the menu sails off with it, anchored to where it was born, which on a
 * phone means it ends up over unrelated content or off screen entirely. Material's own answer
 * is to dismiss the menu, and that is what this does.
 *
 * Implemented as a global mixin keyed on the component name rather than as a DOM sweep: the
 * portaled content element is not the component's root, so `__vue__` is not on it and there is
 * no way back from the node to the instance that owns `isActive`.
 */

const openMenus = new Set<any>()

/** Components whose dropdown is a v-menu we want dismissed. v-dialog is deliberately absent. */
const MENU_NAMES = ['v-menu']

function closeAll() {
  openMenus.forEach(m => {
    if (m.isActive) m.isActive = false
  })
}

function onScroll(e: Event) {
  if (!openMenus.size) return
  const target = e.target as Node | null
  // A scroll *inside* the menu's own list — or inside a dialog, sheet or drawer that moves with
  // its own container — is not the page moving underneath the menu. Note that scroll events do
  // not bubble; they only arrive here because the listener is registered in the capture phase.
  if (target instanceof HTMLElement &&
    target.closest('.v-menu__content, .v-dialog__content, .v-bottom-sheet, .v-navigation-drawer, .v-overlay')) return
  closeAll()
}

export default {
  install(Vue: typeof _Vue) {
    Vue.mixin({
      created() {
        if (MENU_NAMES.includes((this as any).$options.name)) openMenus.add(this)
      },
      beforeDestroy() {
        if (MENU_NAMES.includes((this as any).$options.name)) openMenus.delete(this)
      },
    })
    window.addEventListener('scroll', onScroll, {capture: true, passive: true})
  },
}
